import api from './reducers/api';

/**
 * Formulaires du site → mails (contact, devis, candidature de guide).
 *
 * Le navigateur n'envoie que les champs du formulaire : le backend choisit les
 * destinataires et met en forme les mails avec les modeles CMS (editables dans
 * l'admin). Remplace les anciens gabarits HTML de email-template/ envoyes par
 * /api/mailjet/send-email, route desormais reservee aux admins.
 *
 * Route publique pour les formulaires ouverts a tous, route /me quand la personne
 * est connectee : son nom et son e-mail sont alors lus dans le JWT.
 */

export type QuoteService = 'airport-pickup' | 'transfer' | 'business-trip' | 'cruise';

export interface QuoteDetail {
  label: string;
  value: string | number | null | undefined;
}

export interface PublicContactRequest {
  kind: 'contact';
  source: 'contact' | 'contact-pro';
  fullName: string;
  email: string;
  reason: string;
  reasonKey?: string;
  message: string;
}

export interface QuoteRequest {
  kind: 'quote';
  service: QuoteService;
  customerName: string;
  email?: string | null;
  phone?: string | null;
  message?: string | null;
  /** Lignes affichees a l'equipe, dans l'ordre (libelles en anglais, comme avant). */
  details: QuoteDetail[];
}

export interface UserContactRequest {
  kind: 'contact';
  source: 'account' | 'booking' | 'guide';
  reason?: string;
  reasonKey?: string;
  message: string;
  bookingReference?: string | null;
}

export interface GuideApplicationRequest {
  kind: 'guide-application';
}

const withLanguage = <T extends object>(payload: T, language?: string | null) => ({
  ...payload,
  language: language || 'en_US',
});

export const sendPublicSiteRequest = async (
  payload: PublicContactRequest | QuoteRequest,
  language?: string | null,
) => {
  await api.post('/api/site-requests', withLanguage(payload, language));
};

export const sendUserSiteRequest = async (
  payload: UserContactRequest | GuideApplicationRequest,
  language?: string | null,
) => {
  await api.post('/api/me/site-requests', withLanguage(payload, language));
};

// Cle i18n du motif choisi (la liste deroulante stocke le libelle traduit) : le
// backend en deduit la boite de l'equipe (info@ ou locals@).
export const CONTACT_REASON_KEYS = [
  'contact_reasons_request_quote',
  'contact_reasons_question_booking',
  'contact_reasons_edit_cancel_booking',
  'contact_reasons_about_account',
  'contact_reasons_other_questions',
  'contact_reasons_guide_about_mission',
  'contact_reasons_guide_about_account',
  'contact_reasons_guide_general_asking',
] as const;

export const reasonKeyFor = (reason: string, t: (key: string) => string): string | undefined =>
  CONTACT_REASON_KEYS.find((key) => t(key) === reason);

// Voyageurs sous la forme lue par l'equipe dans les anciens mails de devis.
export const quoteTravellers = (value?: { adults?: number; children?: number; infants?: number } | null) =>
  value ? `${value.adults || 0} adults, ${value.children || 0} children, ${value.infants || 0} infants` : '';

// Fiche d'une demande d'accueil a l'aeroport (page et formulaire mobile), dans
// l'ordre et avec les libelles des anciens mails.
export const airportPickupDetails = ({
  routeType, airport, date, time, selectedTravelers, flightNumber, suitcases, terminal, addressFrom, addressTo,
}: {
  routeType?: string | null;
  airport?: { name?: string; code?: string } | null;
  date?: string | null;
  time?: string | null;
  selectedTravelers?: { adults?: number; children?: number; infants?: number } | null;
  flightNumber?: string | null;
  suitcases?: number | null;
  terminal?: string | null;
  addressFrom?: string | null;
  addressTo?: string | null;
}): QuoteDetail[] => [
  { label: 'Route type', value: routeType },
  { label: 'Selected airport', value: airport ? `${airport.name} (${airport.code})` : 'N/A' },
  { label: 'Date', value: date },
  { label: 'Time', value: time },
  { label: 'Travelers', value: quoteTravellers(selectedTravelers) },
  { label: 'Flight number', value: flightNumber },
  { label: 'Suitcases', value: suitcases },
  { label: 'Terminal', value: terminal },
  { label: 'Address', value: addressFrom || addressTo },
];
